/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1785196800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jul 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "gam360",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Connect to Google",
          "depth": 0,
          "id": "connect-to-google"
        }, {
          "value": "Service account",
          "depth": 1,
          "id": "service-account"
        }, {
          "value": "Google account",
          "depth": 1,
          "id": "google-account"
        }, {
          "value": "Grant bucket access",
          "depth": 1,
          "id": "grant-bucket-access"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing data sets",
          "depth": 1,
          "id": "syncing-data-sets"
        }, {
          "value": "Example exported file",
          "depth": 0,
          "id": "example-exported-file"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      ol: "ol",
      h3: "h3",
      img: "img",
      code: "code",
      h4: "h4",
      h5: "h5",
      em: "em",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PremiumDestination, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Google Ad Manager 360"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-data-sets",
                children: _jsx(_components.strong, {
                  children: "Any data set"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from a source to an Ad Manager cloud storage bucket as a CSV file"
            }), _jsx(_components.td, {
              children: "Insert, All"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.google.com/ad-manager/api/reference/v202411/SegmentPopulationService",
                children: "Upload segment members"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The Google Ad Manager 360 (GAM 360) destination allows you to batch upload identifiers to audience segments in bulk by sending your desired segments to an Ad Manager cloud storage bucket. The segments appear as ", _jsx(_components.a, {
          href: "#example-exported-file",
          children: "a CSV file"
        }), ", which you can then use in GAM 360."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can create the audience segment in Ad Manager for you, or upload identifiers to a segment you created yourself in the Ad Manager console. See ", _jsx(_components.a, {
          href: "#audience-segment",
          children: "Audience segment"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "The integration supports these sync modes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Insert mode creates a CSV file with the rows that were added since the last sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": All mode creates a CSV file with all the rows in the query results, every time the sync runs."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you can use Hightouch to batch upload your identifiers to GAM 360, ensure you've completed these steps, taken from ", _jsx(_components.a, {
          href: "https://support.google.com/admanager/answer/4349785/",
          children: "Google Ad Manager Help"
        }), ":"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Locate and submit your network code. Find your network code in Ad Manager under ", _jsx(_components.strong, {
            children: "Admin → Global settings → Network code"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you want to upload to a segment you created yourself, create an active first-party audience segment to associate the identifiers with. If you don't want this segment to collect users using your own inventory, select \"Publisher Managed\" as the Population method. Skip this step if you want Hightouch to ", _jsx(_components.a, {
            href: "#audience-segment",
            children: "create the segment for you"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://support.google.com/groups/answer/2464926",
            children: "Create a Google Group"
          }), " that contains all Google Accounts that will have access to upload and view files."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Expect to wait up for 24 hours from the time of segment creation to the time\nwhen you can perform batch upload. This applies whether you create the segment\nin the Ad Manager console or Hightouch creates it for you."
        })
      }), "\n", _jsx(_components.p, {
        children: "Once your account manager has completed your setup, you can create the file of identifiers you want to upload."
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google",
        children: "Connect to Google"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google Ad Manager 360"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports two authentication methods:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Method"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Service account"
              })
            }), _jsx(_components.td, {
              children: "A Google Cloud service account writes identifier files to your Ad Manager cloud storage bucket. Use this method when you create audience segments yourself in the Ad Manager console."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Google account (enables automatic segment creation)"
              })
            }), _jsxs(_components.td, {
              children: ["Adds a Google OAuth connection to the Ad Manager API alongside the service account. Required if you want Hightouch to ", _jsx(_components.a, {
                href: "#audience-segment",
                children: "create audience segments"
              }), " for you."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Both methods upload files with a service account, so you provide service account credentials either way."
      }), "\n", _jsx(_components.h3, {
        id: "service-account",
        children: "Service account"
      }), "\n", _jsx(_components.p, {
        children: "Enter your:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCP credentials"
          }), ": if you need to create a cloud credential, consult ", _jsx(_components.a, {
            href: "/security/gcp",
            children: "our documentation"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCP Project ID"
          }), ": you can follow ", _jsx(_components.a, {
            href: "https://support.google.com/googleapi/answer/7014113?hl=en",
            children: "these steps"
          }), " to find it"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCS Bucket Name"
          }), ": this be should just be the name of the bucket, not a URL"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-connect.png",
          alt: "Google Ad Manager 360 setup"
        })
      }), "\n", _jsx(_components.h3, {
        id: "google-account",
        children: "Google account"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Log in to Google Ad Manager 360"
        }), ", sign in with a Google Account, and grant Hightouch access. The account you authorize must have access to your Ad Manager network with permission to manage audience segments."]
      }), "\n", _jsx(_components.p, {
        children: "After you authorize, enter your:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GCP credentials"
          }), ": the service account Hightouch uses to upload identifier files"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "GCP Project ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "GCS Bucket Name"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ad Manager Network Code"
          }), ": the network Hightouch creates audience segments in. Find it in Ad Manager under ", _jsx(_components.strong, {
            children: "Admin → Global settings"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "grant-bucket-access",
        children: "Grant bucket access"
      }), "\n", _jsxs(_components.p, {
        children: ["For either method, run the ", _jsx(_components.code, {
          children: "gcloud"
        }), " command shown under ", _jsx(_components.strong, {
          children: "Grant permissions"
        }), " to grant Hightouch's service account the ", _jsx(_components.a, {
          href: "https://cloud.google.com/storage/docs/access-control/iam-roles#standard-roles",
          children: "Storage Object Admin"
        }), " role. To run this command, you need to install the ", _jsx(_components.a, {
          href: "https://cloud.google.com/sdk/docs/install",
          children: "gcloud CLI tool"
        }), ". Otherwise, if you use ", _jsx(_components.a, {
          href: "https://groups.google.com/",
          children: "Google Groups"
        }), ", you can also ", _jsx(_components.a, {
          href: "https://support.google.com/a/answer/167094",
          children: "assign this role to a group member"
        }), " by using the Admin console or Google Groups."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Ad Manager 360 destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google Ad Manager 360 destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-data-sets",
        children: "Syncing data sets"
      }), "\n", _jsx(_components.h4, {
        id: "list-of-identifiers",
        children: "List of identifiers"
      }), "\n", _jsx(_components.p, {
        children: "The GAM 360 destination supports several different identifier types."
      }), "\n", _jsx(_components.p, {
        children: "In the first mapping section, select the column corresponding to the list of identifiers you want to batch upload on the left. On the right, select the type of identifiers that are being uploaded to each segment."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-identifiers.png",
          alt: "Record matching in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can find the full list of identifiers in ", _jsx(_components.a, {
          href: "https://support.google.com/admanager/answer/4349785?hl=en&sjid=15048174647173915590-NA",
          children: "Google's docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-identifier-types.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "audience-segment",
        children: "Audience segment"
      }), "\n", _jsxs(_components.p, {
        children: ["In the ", _jsx(_components.strong, {
          children: "Audience segment"
        }), " section, choose whether Hightouch creates the audience segment in Ad Manager or uploads identifiers to a segment you created yourself."]
      }), "\n", _jsx(_components.h5, {
        id: "create-the-segment-automatically",
        children: "Create the segment automatically"
      }), "\n", _jsxs(_components.p, {
        children: ["Turn on ", _jsx(_components.strong, {
          children: "Automatically create the audience segment in Google Ad Manager"
        }), ". On the first sync run, Hightouch creates a first-party audience segment in your Ad Manager network and uses its ID as the ", _jsx(_components.code, {
          children: "list_id"
        }), " in every upload. Later runs reuse that same segment."]
      }), "\n", _jsxs(_components.p, {
        children: ["This option requires the ", _jsx(_components.a, {
          href: "#google-account",
          children: _jsx(_components.strong, {
            children: "Google account"
          })
        }), " authentication method and an Ad Manager network code. Hightouch creates the segment through the Ad Manager ", _jsx(_components.a, {
          href: "https://developers.google.com/ad-manager/api/reference/v202411/AudienceSegmentService",
          children: _jsx(_components.code, {
            children: "AudienceSegmentService"
          })
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch creates the segment with no rule, so it collects no users from your own inventory. Membership comes only from the identifiers Hightouch uploads. This matches selecting \"Publisher Managed\" as the population method in the Ad Manager console."
      }), "\n", _jsx(_components.p, {
        children: "Two optional fields control the segment Hightouch creates:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Segment name"
          }), " — Name for the segment in Ad Manager. Defaults to the model name."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "How long should users remain in the segment?"
          }), " — Number of days a user stays in the segment after each upload, from 1 to 540. Defaults to 540."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Because Hightouch reuses the same segment for the life of the sync, changing these fields after the first run doesn't rename or reconfigure the segment in Ad Manager."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Ad Manager can take up to 24 hours after a segment is created before it accepts\nuploads. Sync runs during that window fail, and Hightouch retries the rows on a\nlater run, so no data is lost. See ", _jsx(_components.a, {
            href: "#the-google-ad-manager-audience-segment-was-created-recently",
            children: "the propagation window\nerror"
          }), "."]
        })
      }), "\n", _jsx(_components.h5, {
        id: "use-an-existing-segment",
        children: "Use an existing segment"
      }), "\n", _jsxs(_components.p, {
        children: ["Leave ", _jsx(_components.strong, {
          children: "Automatically create the audience segment in Google Ad Manager"
        }), " turned off. Then denote the audience segment IDs with which the identifiers from the previous section should be associated. You can either specify a column from your model to associate each identifier with a different segment, or a static value to associate all identifiers with the same segment. Use the toggle on the right of the field to flip between these two options."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-gam360-audience.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "enter-filename",
        children: "Enter filename"
      }), "\n", _jsxs(_components.p, {
        children: ["The filename field lets you specify the parent directory and the name of the file you want to use for your results.\nYou can include timestamp variables in the filename, surrounding each with ", _jsx(_components.code, {
          children: "{}"
        }), ".\nHightouch supports these timestamp variables:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YYYY"
          }), ": Represents the full year in four digits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YY"
          }), ": The last two digits of the year."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MM"
          }), ": Two-digit month format (01-12)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DD"
          }), ": Two-digit day format (01-31)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "HH"
          }), ": Two-digit hour format in 24-hour clock (00-23)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "mm"
          }), ": Two-digit minute format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ss"
          }), ": Two-digit second format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ms"
          }), ": Three-digit millisecond format."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "X"
          }), ": Unix timestamp in seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "x"
          }), ": Unix timestamp in milliseconds."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All dates and times are UTC."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you could enter ", _jsx(_components.code, {
          children: "upload/{YYYY}-{MM}-{DD}-{HH}-{mm}-result.json"
        }), " to dynamically include the year, month, date, hour, and minute in each uploaded file. Hightouch would insert each file in the ", _jsx(_components.code, {
          children: "upload"
        }), " directory, which would need to already exist in your bucket."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use other ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable values"
        }), " to include sync metadata in the filename:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.name}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.run.id}"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If a file already exists at the path you entered at the time of a sync,\nHightouch overwrites it."
        })
      }), "\n", _jsx(_components.h4, {
        id: "set-filename-offset",
        children: "Set filename offset"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch uses the timestamp of the sync run to fill in timestamp variables. You can optionally include an offset in seconds. For example, if you want the filename's date to be 24 hours ", _jsx(_components.em, {
          children: "before"
        }), " the sync takes place, enter '-86400' (", _jsx(_components.code, {
          children: "24 hours * 60 minutes * 60 seconds"
        }), ")."]
      }), "\n", _jsx(_components.h4, {
        id: "empty-file-results",
        children: "Empty file results"
      }), "\n", _jsx(_components.p, {
        children: "In insert mode, you can select how Hightouch should handle empty results files. Empty result files can occur if your model's query results haven't changed since the last sync."
      }), "\n", _jsxs(_components.p, {
        children: ["You can select whether to ", _jsx(_components.strong, {
          children: "skip empty files"
        }), ". If you skip empty files, it means Hightouch won't export any files if your model's query results haven't changed since your last sync."]
      }), "\n", _jsx(_components.h2, {
        id: "example-exported-file",
        children: "Example exported file"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example file content for encrypted cookie IDs:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "cookie_encrypted,list_id\nScpJKu-yV8je93qkd32MOA,3153490\nw2gsrUcwxF-OiJTRmQswQA,3153490\n"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Example file content for raw AdIDs:"
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "cookie_adid,list_id\n38400000-8cf0-11bd-b23e-10b96e40000d,3153490\n38400001-8cf1-11be-b23f-10b96e40000e,3153490\n"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Google Ad Manager 360, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error\ncodes: Other destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsxs(_components.p, {
        children: ["If you let Hightouch create the segment, confirm the segment exists in Ad Manager under ", _jsx(_components.strong, {
          children: "Audiences"
        }), " after the first sync run. Membership stays at zero until the propagation window elapses and a later run uploads identifiers, so check the member count again the following day."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you use an existing segment, confirm the ", _jsx(_components.code, {
          children: "list_id"
        }), " in the ", _jsx(_components.a, {
          href: "#example-exported-file",
          children: "exported file"
        }), " matches the segment ID in Ad Manager."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "the-google-ad-manager-audience-segment-was-created-recently",
        children: "The Google Ad Manager audience segment was created recently"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["The Google Ad Manager audience segment was created recently. Ad Manager can take up to 24 hours before a newly created segment accepts uploads", _jsx(_components.span, {
            className: "hljs-comment",
            children: "; this sync will complete automatically on its next run after that window."
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Hightouch created the audience segment less than 25 hours ago, which is Ad Manager's 24-hour propagation window plus a one-hour buffer. Ad Manager ignores uploads to a segment that hasn't finished propagating. Hightouch fails the run rather than upload a file Ad Manager would discard."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " No action needed. The rows stay unsynced, and the sync completes on its next scheduled run after the window elapses. Segments you created manually and referenced by list ID are never gated this way."]
      }), "\n", _jsx(_components.h4, {
        id: "automatic-segment-creation-requires-the-google-account-method",
        children: "Automatic segment creation requires the Google account method"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-java",
          children: ["Automatic segment creation ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "requires"
          }), " connecting ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "this"
          }), " destination with the ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "Google"
          }), " ", _jsx(_components.span, {
            className: "hljs-params",
            children: "(OAuth)"
          }), " authentication method.\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sync has ", _jsx(_components.strong, {
          children: "Automatically create the audience segment in Google Ad Manager"
        }), " turned on, but the destination uses the ", _jsx(_components.strong, {
          children: "Service account"
        }), " method, which has no Ad Manager API credentials."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Reconnect the destination using the ", _jsx(_components.a, {
          href: "#google-account",
          children: _jsx(_components.strong, {
            children: "Google account"
          })
        }), " method, or turn off automatic creation and ", _jsx(_components.a, {
          href: "#use-an-existing-segment",
          children: "enter a list ID"
        }), " instead."]
      }), "\n", _jsx(_components.h4, {
        id: "an-ad-manager-network-code-is-required",
        children: "An Ad Manager network code is required"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["An Ad Manager network ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "code"
          }), " is required ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " automatically create audience segments. Add it ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " the destination settings.\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The destination is connected with the Google account method, but the ", _jsx(_components.strong, {
          children: "Ad Manager Network Code"
        }), " field is empty."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Go to your destination settings and enter your network code. Find it in Ad Manager under ", _jsx(_components.strong, {
          children: "Admin → Global settings"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "a-google-ad-manager-list-id-is-required",
        children: "A Google Ad Manager list ID is required"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["A Google Ad Manager list ID ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "is"
          }), " required ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "when"
          }), " automatic segment creation ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "is"
          }), " disabled.\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Automatic segment creation is turned off and the sync has no list ID column or static value."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Edit the sync's ", _jsx(_components.strong, {
          children: "Audience segment"
        }), " section and either enter a list ID or turn on automatic segment creation."]
      }), "\n", _jsx(_components.h4, {
        id: "ad-manager-api-errors",
        children: "Ad Manager API errors"
      }), "\n", _jsx(_components.p, {
        children: "When automatic segment creation fails, Ad Manager's own error appears in the sync logs, for example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "AuthenticationError.NETWORK_NOT_FOUND\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Ad Manager rejected the request to create the segment. ", _jsx(_components.code, {
          children: "NETWORK_NOT_FOUND"
        }), " means the network code is wrong or the Google Account you authorized doesn't belong to that network."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Verify the network code in your destination settings, and confirm the authorized Google Account has access to that Ad Manager network with permission to manage audience segments. For other codes, see Ad Manager's ", _jsx(_components.a, {
          href: "https://developers.google.com/ad-manager/api/reference/v202411/AudienceSegmentService.ApiError",
          children: "API error reference"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
